import { queryOptions, useQuery, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import type { ReportingWebhook as UIAPIReportingWebhook } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseWebhook, webhookKeys, type Webhook } from './entity';

/**
 * Reporting-webhook reads. Same bulletproof shape as the rest of the layer:
 * a plain async fetcher, a `getXQueryOptions` factory, and a thin suspense hook.
 *
 * Both reads use `staleTime: 0`. The Ember list route re-`findAll`s on every
 * visit (it drives create/enable validation), and the edit route always wants
 * the current row — so neither should serve a stale cache entry.
 */

const STALE_TIME = 0;

/** The single-webhook response carries an optional `webhook`; assert it. */
function requireWebhook(res: { webhook?: UIAPIReportingWebhook }): UIAPIReportingWebhook {
  if (res.webhook == null) {
    throw new Error('reporting webhook response did not include a webhook');
  }
  return res.webhook;
}

/** @public */
export async function getWebhooks(workspaceId: number, qc: QueryClient): Promise<Webhook[]> {
  const data = await hydraUiApi.reportingWebhooks.getWebhooks({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.webhooks ?? []).map(parseWebhook);
  // Sideload each row into its detail slot so a follow-up edit read resolves
  // from cache without an extra GET.
  for (const w of parsed) {
    qc.setQueryData(webhookKeys.detail(workspaceId, w.id), w);
  }
  return parsed;
}

/** @public */
export const getWebhooksQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    // `TListParams = void` for this entity — the no-filters list slot.
    queryKey: webhookKeys.list(workspaceId, undefined),
    queryFn: () => getWebhooks(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseWebhooksOptions {
  /**
   * Use this id instead of reading the URL. Callers that already hold a validated id pass it in —
   * required for any page that renders inside a Suspense boundary, because React can begin
   * rendering the child before the parent has committed, and the URL may still be mid-redirect.
   */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getWebhooksQueryOptions>;
}

/** @public */
export const useWebhooks = ({ workspaceId: overrideWorkspaceId, queryConfig }: UseWebhooksOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getWebhooksQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/** Stable identity so callers can put `webhooks` in a dependency array without re-firing. */
const EMPTY_WEBHOOKS: readonly Webhook[] = [];

/**
 * The same list, read as an auxiliary signal rather than a prerequisite: it powers the
 * duplicate-endpoint notice and one analytics property, and a form that renders without either is
 * far better than no form at all. Unlike `useWebhooks` this neither suspends nor throws — while it
 * is in flight or after it has failed, callers see an empty list and `isLoaded: false`.
 *
 * This mirrors Ember, whose create and edit routes kick `fetchWebhooks` off from `setupController`
 * instead of the model hook and swallow its failure outright: "not critical and only for
 * validations".
 *
 * @public
 */
export const useOptionalWebhooks = ({ workspaceId }: { workspaceId: number }) => {
  const qc = useQueryClient();
  const { data, isSuccess } = useQuery({
    ...getWebhooksQueryOptions(workspaceId, qc),
    // One attempt, like Ember's single un-retried fetch. Retrying would only delay the form's
    // duplicate hints; nothing here is worth a backoff.
    retry: false,
  });

  return { webhooks: data ?? EMPTY_WEBHOOKS, isLoaded: isSuccess };
};

/** @public */
export async function getWebhook(workspaceId: number, webhookId: string): Promise<Webhook> {
  const data = await hydraUiApi.reportingWebhooks.getWebhooks2({
    params: { path: { environment_id: String(workspaceId), id: webhookId } },
  });
  return parseWebhook(requireWebhook(data));
}

/** @public */
export const getWebhookQueryOptions = (workspaceId: number, webhookId: string) =>
  queryOptions({
    queryKey: webhookKeys.detail(workspaceId, webhookId),
    queryFn: () => getWebhook(workspaceId, webhookId),
    staleTime: STALE_TIME,
  });

interface UseWebhookOptions {
  id: string;
  /** Same override as `useWebhooks`, and required for the same reason. */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getWebhookQueryOptions>;
}

/** @public */
export const useWebhook = ({ id, workspaceId: overrideWorkspaceId, queryConfig }: UseWebhookOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useSuspenseQuery({
    ...getWebhookQueryOptions(workspaceId, id),
    ...queryConfig,
  });
};
